import { Slot, SlotType, VenueType } from '@thurstech/commons/dist/entity/venue';
import _ from 'lodash';
import React from 'react';
import {
  FieldErrors,
  FieldNamesMarkedBoolean,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch
} from 'react-hook-form';

import Button from '../../../components/button/Button';
import Modal from '../../../components/common/Modal';
import Input from '../../../components/input/Input';
import InputDate from '../../../components/input/InputDate';
import { useAppDispatch, useAppSelector } from '../../../hooks/store';
import { successMessage } from '../../../hooks/useNotifications';
import { isThunkLoading } from '../../../redux/common';
import {
  addSlotThunk,
  getPackageOverridesThunk,
  updatePackageOverridesThunk,
  updateSlotThunk
} from '../../../redux/reducer/listingSlice';
import { withErrorHandler } from '../../../utils/utils';
import { SlotsFormData } from '../../../validation/operations/addOrEditProduct/ticketBooking/slotsSchema';

interface SlotsModalProps {
  index: number | undefined;
  isOpen: boolean;
  resetState: () => void;
  onClose: () => void;
  listingId: string;
  venueId: string;
  isResourcesLinkedToVenue?: boolean;
  register: UseFormRegister<SlotsFormData>;
  errors: FieldErrors<SlotsFormData>;
  watch: UseFormWatch<SlotsFormData>;
  getValues: UseFormGetValues<SlotsFormData>;
  dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<SlotsFormData>>>;
  trigger: UseFormTrigger<SlotsFormData>;
  setValue: UseFormSetValue<SlotsFormData>;
}

const SlotsModal: React.FC<SlotsModalProps> = ({
  index,
  isOpen,
  onClose,
  listingId,
  venueId,
  isResourcesLinkedToVenue,
  resetState,
  watch,
  getValues,
  dirtyFields,
  register,
  setValue,
  errors,
  trigger
}) => {
  const isLoading = useAppSelector(state => state.listing.isLoading);
  const dispatch = useAppDispatch();
  const commonProps = {
    register,
    errors,
    required: true
  };

  if (index === undefined) {
    return null;
  }

  const packageOverrides = watch('packageOverrides') ?? [];
  const slot = getValues(`slots.${index}`) as SlotType;
  const packageOverridesError = _.get(errors, 'packageOverrides')?.message as string;
  return (
    <Modal
      isOpen={isOpen}
      width="w-4/5"
      onClose={() => {
        resetState();
        onClose();
      }}
      header="Add Slot"
    >
      <div className="ml-6 mt-2 space-y-3">
        <Input {...commonProps} hidden name={`slots.${index}.id`} type="text" />
        <div className="grid grid-cols-3 gap-6">
          <InputDate
            {...commonProps}
            label="Start Time"
            name={`slots.${index}.sessions.0.0.from`}
            watch={watch}
            setValue={setValue}
            readOnly={slot?.isLive}
            showTimeSelect
          />
          <InputDate
            {...commonProps}
            label="End Time"
            name={`slots.${index}.sessions.0.0.to`}
            watch={watch}
            setValue={setValue}
            readOnly={slot?.isLive}
            showTimeSelect
          />
          {isResourcesLinkedToVenue && (
            <Input
              {...commonProps}
              topLabel
              label="Tickets"
              name={`slots.${index}.resources.count`}
              type="number"
            />
          )}
        </div>

        <div className="bg-zinc-100 p-4">
          <h2 className="mb-4  text-xl font-semibold">Package Overrides</h2>
          <div className="grid grid-cols-3 gap-6 mb-4">
            {packageOverrides?.map((_, i) => (
              <div key={i} className="bg-white rounded w-full p-2">
                <div className="mb-2">
                  <span className="font-bold text-xl">{watch(`packageOverrides.${i}.title`)}</span>
                </div>
                <Input {...commonProps} readOnly hidden name={`packageOverrides.${i}.id`} type="text" />
                <Input
                  {...commonProps}
                  label="Price"
                  name={`packageOverrides.${i}.price`}
                  type="number"
                  prefix="₹"
                  className="text-right"
                />
                <Input
                  {...commonProps}
                  label="Discount"
                  name={`packageOverrides.${i}.discount`}
                  type="number"
                  suffix="%"
                  className="text-right"
                />
                {!isResourcesLinkedToVenue && (
                  <Input
                    {...commonProps}
                    label={'Total Tickets'}
                    name={`packageOverrides.${i}.resources.count`}
                    type="number"
                    className="text-right"
                  />
                )}
              </div>
            ))}
          </div>

          {packageOverridesError && (
            <p className="text-red-600 text-sm font-semibold mt-1">{packageOverridesError}</p>
          )}
        </div>

        <div className="mt-2">
          <Button
            label="Add"
            type="button"
            isLoading={isThunkLoading(
              isLoading,
              addSlotThunk,
              updateSlotThunk,
              getPackageOverridesThunk,
              updatePackageOverridesThunk
            )}
            onClick={async () => {
              const isValid = await trigger([`slots.${index}`, 'packageOverrides']);

              const packageOverrides = getValues('packageOverrides')!;
              console.log(errors, packageOverrides);
              if (isValid) {
                const slot = getValues(`slots.${index}`);
                const packageOverrides = getValues('packageOverrides')!;
                // const updatedFields = Object.keys(dirtyFields).reduce((acc, key) => {
                //   acc[key] = getValues(key);
                //   return acc;
                // }, {});

                console.log('Updated Fields:', dirtyFields);
                await withErrorHandler(async () => {
                  if (venueId) {
                    if (!slot.id) {
                      const { id } = await dispatch(
                        addSlotThunk({
                          listingId,
                          venueId,
                          slot,
                          isResourcesLinkedToVenue: isResourcesLinkedToVenue
                        })
                      ).unwrap();
                      if (id) {
                        slot.id = id;
                        setValue(`slots.${index}.id`, id);
                      }
                    } else {
                      await dispatch(
                        updateSlotThunk({
                          listingId,
                          venueId,
                          slot: slot as Slot & { id: string },
                          isResourcesLinkedToVenue: isResourcesLinkedToVenue
                        })
                      ).unwrap();
                    }
                    await dispatch(
                      updatePackageOverridesThunk({
                        listingId,
                        venueId,
                        data: {
                          listingId,
                          venueId,
                          venueType: VenueType.TICKET_BOOKING,
                          details: isResourcesLinkedToVenue
                            ? packageOverrides.map(override => _.pick(override, 'id', 'price', 'discount'))
                            : packageOverrides.map(override =>
                                _.pick(override, 'id', 'price', 'discount', 'resources')
                              ),
                          slotId: slot.id!
                        }
                      })
                    ).unwrap();
                    successMessage('Successfully Saved slot');
                  }
                  onClose();
                });
              }
            }}
            className="font-medium bg-[#35e0a1] py-2 px-10 mt-6 rounded-md"
          />
        </div>
      </div>
    </Modal>
  );
};

export default SlotsModal;
